import { useState, useEffect } from 'react';

// The header's bottom-docked nav bar; not rendered by the Sticky component.
// See .claude/context/sticky-offset.md
const FOOTER_NAV_SELECTOR = '#header-nav-container';

// Our own mount root — excluded or the lift would feed back on itself.
const OWN_ROOT_SELECTOR = '#magic-apron-loader';

// Matches the Sticky component's rendered DOM signature. See .claude/context/sticky-offset.md
const STICKY_CONSUMER_SELECTOR = '[style^="min-height"] > [class*="sticky"].sui-bottom-0';

// Fixed bottom bars the signature above can't match; each must be listed by hand.
// See .claude/context/sticky-offset.md
const FIXED_BOTTOM_BAR_SELECTORS = [
  '[data-automation-id="checkoutNowButton"]',
];

const getViewportHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 0);

// Hidden/slid-away must read as "not intruding" (0), not "full viewport height".
// See .claude/context/sticky-offset.md
const intrusionFromTop = (top, viewportHeight) => (
  Number.isFinite(top) && top > 0 && top < viewportHeight ? viewportHeight - top : 0
);

// Intrusion of an element from the bottom of the viewport, or 0 when it is
// hidden, detached, or collapsed (which all report an empty, all-zero rect).
const getElementIntrusion = (element, viewportHeight) => {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return 0;
  return intrusionFromTop(rect.top, viewportHeight);
};

// Max intrusion across every Sticky consumer. See .claude/context/sticky-offset.md
const getStickyConsumerIntrusion = (viewportHeight) => {
  const consumers = document.querySelectorAll(STICKY_CONSUMER_SELECTOR);
  let maxIntrusion = 0;
  consumers.forEach((element) => {
    // Skip the consumers we render ourselves and any that are hidden.
    if (element.closest(OWN_ROOT_SELECTOR)) return;
    if (element.classList.contains('sui-hidden')) return;
    maxIntrusion = Math.max(maxIntrusion, getElementIntrusion(element, viewportHeight));
  });
  return maxIntrusion;
};

const getFixedBarIntrusion = (viewportHeight) => FIXED_BOTTOM_BAR_SELECTORS.reduce(
  (maxIntrusion, selector) => Math.max(
    maxIntrusion,
    getElementIntrusion(document.querySelector(selector), viewportHeight),
  ),
  0,
);

// The bottom nav only intrudes while it is actually docked at the bottom
// (data-position-bottom); otherwise it lives in the normal header flow.
const getFooterIntrusion = (viewportHeight) => {
  const nav = document.querySelector(FOOTER_NAV_SELECTOR);
  if (!nav || !('positionBottom' in nav.dataset)) return 0;
  return getElementIntrusion(nav, viewportHeight);
};

export const useStickyElementOffset = () => {
  const [offsetMargin, setOffsetMargin] = useState(0);

  useEffect(() => {
    let frame = null;
    // Self-feedback guard: ignore MutationObserver bursts that are only our own writes.
    // See .claude/context/sticky-offset.md
    const ownRoot = document.querySelector(OWN_ROOT_SELECTOR);

    const measure = () => {
      const viewportHeight = getViewportHeight();
      // Lift above whichever bar pokes up highest; several may be present at once.
      setOffsetMargin(Math.max(
        getFooterIntrusion(viewportHeight),
        getStickyConsumerIntrusion(viewportHeight),
        getFixedBarIntrusion(viewportHeight),
      ));
    };

    const schedule = (records) => {
      // MutationObserver passes a records array; the event listeners below pass
      // an Event. Only records can be "ours", so only they get filtered.
      if (Array.isArray(records) && ownRoot
        && records.every((record) => ownRoot.contains(record.target))) {
        return;
      }
      // Coalesce bursts into one rAF-scheduled measurement, not one per event.
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    // Observe the whole app root to catch any consumer appearing anywhere post-hydration.
    const observerRoot = document.querySelector('#root');
    const observer = new MutationObserver(schedule);
    if (observerRoot) {
      observer.observe(observerRoot, { attributes: true, childList: true, subtree: true });
    }

    // Footer slide/resize don't mutate the DOM; listen directly. See sticky-offset.md
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('transitionend', schedule);

    measure();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('transitionend', schedule);
    };
  }, []);

  return offsetMargin;
};
